import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  UserPlus,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Copy,
  Loader2,
  Info,
  Plus
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  invited_by: string;
  invited_at: string;
  expires_at: string;
  project_id?: string;
  project_name?: string;
  token: string;
}

export default function InvitationsScreen() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch invitations
  const { data: invitations = [], isLoading } = useQuery({
    queryKey: ['/api/admin/invitations'],
    queryFn: () => apiRequest('/api/admin/invitations')
  });

  // Fetch projects for invitation
  const { data: projects = [] } = useQuery({
    queryKey: ['/api/admin/projects'],
    queryFn: () => apiRequest('/api/admin/projects')
  });

  // Create invitation mutation
  const createInvitationMutation = useMutation({
    mutationFn: (data: { email: string; role: string; project_id?: string }) =>
      apiRequest('/api/admin/invitations', {
        method: 'POST',
        body: data
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/invitations'] });
      setIsCreateOpen(false);
      toast({
        title: "Success",
        description: "Invitation sent successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send invitation",
        variant: "destructive"
      });
    }
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest(`/api/admin/invitations/${invitationId}/resend`, {
        method: 'POST'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/invitations'] });
      toast({
        title: "Success",
        description: "Invitation resent successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to resend invitation",
        variant: "destructive"
      });
    }
  });

  // Revoke invitation mutation
  const revokeInvitationMutation = useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest(`/api/admin/invitations/${invitationId}/revoke`, {
        method: 'POST'
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/invitations'] });
      toast({
        title: "Success",
        description: "Invitation revoked successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to revoke invitation",
        variant: "destructive"
      });
    }
  });

  const handleCreateInvitation = (formData: FormData) => {
    const data = {
      email: formData.get('email') as string,
      role: formData.get('role') as string,
      project_id: (() => {
        const projectId = formData.get('project_id') as string;
        return projectId === 'platform-access' || !projectId ? undefined : projectId;
      })()
    };
    createInvitationMutation.mutate(data);
  };

  const copyInvitationLink = (token: string) => {
    const link = `${window.location.origin}/invitation/${token}`;
    navigator.clipboard.writeText(link);
    toast({
      title: "Success",
      description: "Invitation link copied to clipboard"
    });
  };

  const roles = ['user', 'admin', 'project_manager'];
  const statuses = ['pending', 'accepted', 'expired', 'revoked'];

  // Filter invitations
  const filteredInvitations = invitations.filter((invitation: Invitation) => {
    return filterStatus === 'all' || invitation.status === filterStatus;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'expired':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'revoked':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Invitations</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage user invitations and access to the platform
          </p>
        </div>
        <Button 
          onClick={() => setIsCreateOpen(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Send Invitation
        </Button>
      </div>

      {/* Guidelines */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/20">
        <CardHeader>
          <CardTitle className="flex items-center text-blue-800 dark:text-blue-200">
            <Info className="w-5 h-5 mr-2" />
            Invitation Management Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Invitation Types:</h4>
              <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                <li>• <strong>Platform Access:</strong> General platform access for any project</li>
                <li>• <strong>Project-Specific:</strong> Access limited to specific projects</li>
                <li>• <strong>Role-Based:</strong> Different permission levels (user, admin, project manager)</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Status Meanings:</h4>
              <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                <li>• <strong>Pending:</strong> Invitation sent, awaiting user acceptance</li>
                <li>• <strong>Accepted:</strong> User has joined and account is active</li>
                <li>• <strong>Expired:</strong> Invitation link has expired</li>
                <li>• <strong>Revoked:</strong> Invitation canceled by admin</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Best Practices:</h4>
              <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                <li>• Set appropriate role permissions</li>
                <li>• Monitor invitation status regularly</li>
                <li>• Revoke unused invitations</li>
                <li>• Follow up on pending invitations</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats and Filters */}
      <div className="grid md:grid-cols-5 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Invitations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invitations.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {invitations.filter((i: Invitation) => i.status === 'pending').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Accepted</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {invitations.filter((i: Invitation) => i.status === 'accepted').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Expired</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {invitations.filter((i: Invitation) => i.status === 'expired').length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Filter Status</CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statuses.map(status => (
                  <SelectItem key={status} value={status} className="capitalize">
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>

      {/* Invitations Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Invitations ({filteredInvitations.length})</CardTitle>
          <CardDescription>
            Manage sent invitations and track user onboarding progress
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="ml-2">Loading invitations...</span>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Invited By</TableHead>
                  <TableHead>Sent Date</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvitations.map((invitation: Invitation) => (
                  <TableRow key={invitation.id}>
                    <TableCell className="font-medium">{invitation.email}</TableCell>
                    <TableCell className="capitalize">{invitation.role}</TableCell>
                    <TableCell>{invitation.project_name || 'Platform Access'}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(invitation.status)}
                        <span className="capitalize">{invitation.status}</span>
                      </div>
                    </TableCell>
                    <TableCell>{invitation.invited_by}</TableCell>
                    <TableCell>{new Date(invitation.invited_at).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(invitation.expires_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        {invitation.status === 'pending' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyInvitationLink(invitation.token)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => resendInvitationMutation.mutate(invitation.id)}
                              disabled={resendInvitationMutation.isPending}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => revokeInvitationMutation.mutate(invitation.id)}
                              disabled={revokeInvitationMutation.isPending}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Invitation Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-md">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleCreateInvitation(new FormData(e.target as HTMLFormElement));
          }}>
            <DialogHeader>
              <DialogTitle>Send User Invitation</DialogTitle>
              <DialogDescription>
                Invite a new user to join the platform with specified role and access
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="user@company.com"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="role">Role</Label>
                <Select name="role" required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role} value={role} className="capitalize">
                        {role.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="project_id">Project Access (Optional)</Label>
                <Select name="project_id">
                  <SelectTrigger>
                    <SelectValue placeholder="Platform Access (All Projects)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="platform-access">Platform Access (All Projects)</SelectItem>
                    {projects.map((project: any) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="submit" disabled={createInvitationMutation.isPending}>
                {createInvitationMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}